import { createFeatureSelector, createSelector } from "@ngrx/store";
import {
  AppField,
  Barcode,
  Doc,
  Fonts,
  FormProperties,
  ImportedService,
  ServiceObj,
  ServiceMapping,
  Trigger,
  View,
  OnHandler,
  PrintSetting,
  MirrorGroups,
  PopupFieldData,
  PopupDetails,
  StageDefaultInfo,
  PopupTableDetails,
  PopupType,
} from "src/app/features/designer-studio/edit-pdf-doc/models/doc.model";
import { DOC_FEATURE_KEY, EditFormState } from "../reducers/doc.reducer";
import { Config, WorkAreasList } from "../../../settings/settings.model";
import { FieldPropertiesMapping } from "../../models/field-properties.model";
import { SelectedService } from "../../../shared/models/Service.model";

export const getEditPdfDocState =
  createFeatureSelector<EditFormState>(DOC_FEATURE_KEY);

// Doc Selectors
export const getDoc = createSelector(getEditPdfDocState, (state): Doc => {
  return state.doc;
});

export const getJsFunctions = createSelector(getEditPdfDocState, (state) => {
  return state.doc?.jsFunctions || {};
});

export const getJsVariables = createSelector(getEditPdfDocState, (state) => {
  return state.doc?.jsVariables || {};
});

export const getConfig = createSelector(getEditPdfDocState, (state) => {
  return state?.config || null;
});

export const getDocType = createSelector(getEditPdfDocState, (state) => {
  return state.doc?.appType || null;
});

// Returns the current selected field ID as string
export const getSelectedFieldId = createSelector(
  getEditPdfDocState,
  (state): string => state.currentSelectedFieldId
);

// Returns the current selected field ID as string
export const getSelectedRadioId = createSelector(
  getEditPdfDocState,
  (state): string => state.currentSelectedRadioId
);

// Returns the views as an object {VIEW_NAME: viewData}
export const getViews = createSelector(
  getEditPdfDocState,
  (state): { [key: string]: View } => {
    return state.views;
  }
);

// To save doc
export const getMergedDoc = createSelector(getDoc, getViews, (doc, views) => {
  const parsedViews = [];
  for (const [, value] of Object.entries(views)) {
    parsedViews.push(value);
  }
  return {
    ...doc,
    views: parsedViews,
  };
});

export const getViewNamesAndOrder = createSelector(
  getDoc,
  getViews,
  (doc: Doc, views): any[] => {
    return doc
      ? doc.onLoad.map((viewName: string) => {
          return {
            viewName,
            type: views[viewName].viewType,
            isPopupTable:
              views[viewName].viewType === "POPUP" &&
              views[viewName].popupDetails.popupType === PopupType.TABLE,
          };
        })
      : [];
  }
);

// Returns the current view ID as a string
export const getCurrentViewId = createSelector(
  getEditPdfDocState,
  (state) => state.currentViewName
);

// Return the selected field object in the current view
export const getSelectedField = createSelector(
  getViews,
  getSelectedFieldId,
  getCurrentViewId,
  (views, selectedFieldId, currentView): AppField => {
    if (views && selectedFieldId && currentView) {
      return views[currentView].appFields[selectedFieldId];
    }
  }
);

export const getPopupStageData = createSelector(
  getViews,
  getCurrentViewId,
  (views, currentView): StageDefaultInfo => {
    if (views && currentView) {
      return views[currentView].popupDetails.stageDefaultInfo;
    }
  }
);

export const getPopupTableDetails = createSelector(
  getViews,
  getCurrentViewId,
  (views, currentView): PopupTableDetails => {
    if (views && currentView) {
      return views[currentView].popupDetails.table;
    }
  }
);

export const getActivePopupField = createSelector(
  getViews,
  getSelectedFieldId,
  getCurrentViewId,
  (views, selectedFieldId, currentView): PopupFieldData => {
    if (views && selectedFieldId && currentView) {
      return views[currentView].popupDetails.data.find(
        (data) => data.id === selectedFieldId
      );
    }
  }
);

export const getCurrentView = createSelector(
  getCurrentViewId,
  getViews,
  (currentViewId: string, views: { [key: string]: View }): View => {
    return views ? views[currentViewId] : null;
  }
);

export const getCurrentViewAppFields = createSelector(
  getCurrentView,
  (currentView: View): { [key: string]: AppField } => {
    if (currentView) {
      return currentView.appFields;
    }
  }
);

export const getCurrentViewPopupDetails = createSelector(
  getCurrentView,
  (currentView: View): PopupDetails => {
    if (currentView) {
      return currentView.popupDetails;
    }
  }
);

export const getImportedServices = createSelector(getCurrentView, (view) =>
  view ? view.importedServices : null
);

export const getCurrentViewOnloadServices = createSelector(
  getCurrentView,
  (view: View): OnHandler => {
    return view.onLoad;
  }
);

export const getCurrentViewOnSubmitServices = createSelector(
  getCurrentView,
  (view: View): OnHandler => {
    return view.onSubmit;
  }
);

export const getCurrentViewFormOnServices = (formView) =>
  createSelector(getCurrentView, (view: View): OnHandler => {
    return view[formView];
  });

export const getCurrentViewType = createSelector(
  getViews,
  getCurrentViewId,
  (views, currentView) => {
    if (views && currentView && views[currentView]) {
      return views[currentView].viewType;
    }
  }
);

export const getCurrentService = createSelector(
  getEditPdfDocState,
  (state): SelectedService => {
    return state.currentService;
  }
);

export const getCurrentServiceData = createSelector(
  getViews,
  getCurrentViewId,
  getCurrentService,
  (views, currentView, service): ServiceObj => {
    if (views && currentView && service) {
      return views[currentView].importedServices[service.groupIndex]?.services[
        service.serviceIndex
      ];
    }
  }
);

export const getCurrentRqMapping = createSelector(
  getViews,
  getCurrentViewId,
  getCurrentService,
  (views, currentView, service): ServiceMapping => {
    if (views && currentView && service) {
      const svc =
        views[currentView].importedServices[service.groupIndex]?.services[
          service.serviceIndex
        ];
      return svc && svc.serviceContexts && svc.serviceContexts[service.context]
        ? svc.serviceContexts[service.context].rqMapping
        : null;
    }
  }
);

export const getCurrentRsMapping = createSelector(
  getViews,
  getCurrentViewId,
  getCurrentService,
  (views, currentView, service): ServiceMapping => {
    if (views && currentView && service) {
      const svc =
        views[currentView].importedServices[service.groupIndex]?.services[
          service.serviceIndex
        ];
      return svc && svc.serviceContexts && svc.serviceContexts[service.context]
        ? svc.serviceContexts[service.context].rsMapping
        : null;
    }
  }
);

export const getCurrentContextTriggers = createSelector(
  getViews,
  getCurrentViewId,
  getCurrentService,
  (views, currentView, service) => {
    if (views && currentView && service) {
      const svc = views[currentView].importedServices[service.groupIndex];
      const data =
        svc &&
        svc.services &&
        svc.services[service.serviceIndex].serviceContexts
          ? svc.services[service.serviceIndex].serviceContexts[service.context]
          : null;
      return { PRE: data?.preHandler || null, POST: data?.postHandler || null };
    }
  }
);

export const getCurrentViewImportedServices = createSelector(
  getCurrentView,
  (currentView: View): ImportedService[] => {
    return currentView?.importedServices || [];
  }
);

export const getCurrentFieldTriggers = createSelector(
  getSelectedField,
  (currentSelectedField: AppField): Trigger | Record<string, never> => {
    return currentSelectedField?.fieldTriggers ?? {};
  }
);

// Return the current views mirror groups
export const getCurrentViewMirrorGroups = createSelector(
  getCurrentView,
  (currentView: View): MirrorGroups | Partial<MirrorGroups> => {
    return currentView?.mirrorGroups || {};
  }
);

// Returns data specific to font styling for the current selected field
export const getFieldPropertiesFontStyling = createSelector(
  getSelectedField,
  (field): Fonts | Record<string, never> => {
    return field ? field.fonts : {};
  }
);

export const getBarcode = createSelector(getDoc, (doc): Barcode => doc.barcode);

export const getFieldDerivedData = createSelector(
  getSelectedField,
  (currentSelectedField) => {
    return currentSelectedField
      ? {
          derived: currentSelectedField.derived,
          valueExpression: currentSelectedField.valueExpression,
        }
      : null;
  }
);

export const getFieldValidator = createSelector(
  getSelectedField,
  (currentSelectedField) => {
    if (currentSelectedField) {
      return currentSelectedField.fieldDataType.validator;
    }

    return null;
  }
);

// Returns data needed for field properties mapping view
export const getFieldPropertiesMappingData = createSelector(
  getSelectedField,
  (currentField): FieldPropertiesMapping => {
    if (currentField) {
      return {
        type: currentField.fieldDataType.type,
        format: currentField.fieldDataType.format,
        defaultValue: currentField.defaultValue,
        maxLength: currentField.fieldDataType.maxLength,
        readOnly: currentField.readOnly,
        maskOnPrint: currentField.maskOnPrint,
        maskOnImaging: currentField.maskOnImaging,
        advancedMasking: currentField.mask,
        required: currentField.fieldDataType.required,
        signInitial: currentField.signInitial,
        signatureDateField: currentField.signatureDateField,
        value: currentField.value,
        options: currentField.options.map((option) => ({
          id: option.id || "",
          oldValue: option.oldValue || "",
          newValue: option.newValue || "",
        })),
      };
    }

    return null;
  }
);

// Returns all fields needed for form properties view
export const getFormProperties = createSelector(
  getDoc,
  (doc: Doc): FormProperties => {
    if (doc) {
      return {
        appName: doc.appName,
        appDisplayName: doc.appDisplayName,
        appDecription: doc.appDecription,
        addBorderToInputs: doc.addBorderToInputs,
        autoPrint: doc.autoPrint,
        disableShareLoanLoanAppPopup: doc.disableShareLoanLoanAppPopup,
        imagingDocName: doc.imagingDocName,
        imagingDocType: doc.imagingDocType,
        emailTemplate: doc.emailTemplate,
        defaultFont: doc.defaultFont,
        logo: doc.logo,
        barcode: doc.barcode,
        imagingIndex: doc.imagingIndex,
        printSetting: doc.printSetting,
      };
    }
  }
);

export const getFormName = createSelector(getDoc, (doc: Doc): string => {
  return doc?.appName || "";
});

export const getVersion = createSelector(getDoc, (doc: Doc): string => {
  return doc?.majorVersion || "";
});

export const getDisplayName = createSelector(getDoc, (doc: Doc): string => {
  return doc?.majorVersion
    ? doc.appDisplayName + " - " + doc.majorVersion
    : doc.appDisplayName;
});

export const getFormPrintSettings = createSelector(
  getFormProperties,
  (formProperties: FormProperties): PrintSetting | null =>
    formProperties ? formProperties.printSetting : null
);

export const getConfiguredPrinters = createSelector(
  getConfig,
  (config: Config): PrintSetting[] | null =>
    config ? config.printSettings : null
);

export const getIsLoading = createSelector(
  getEditPdfDocState,
  (state) => state.formLoading
);

export const getFormCatalogs = createSelector(getDoc, (doc: Doc): string[] => {
  return doc?.catalogs || [];
});

export const getFormWorkAreas = createSelector(getDoc, (doc: Doc): string[] => {
  return doc?.workAreas || [];
});

export const getWorkAreaList = createSelector(
  getConfig,
  (config: Config): WorkAreasList[] => {
    return !!config ? config.metadata.workAreasList : [];
  }
);

export const getFormPermissions = createSelector(
  getDoc,
  (doc: Doc): string[] => {
    return doc?.permissions || [];
  }
);

export const getFormPrivilegeGroups = createSelector(
  getDoc,
  (doc: Doc): number[] => {
    return doc?.privilegeGroups || [];
  }
);

export const getFormBarcode = createSelector(getDoc, (doc: Doc): Barcode => {
  return doc?.barcode || null;
});

export const getHTMLElement = createSelector(
  getEditPdfDocState,
  getSelectedField,
  (state: EditFormState, currentSelectedField): any | null => {
    if (currentSelectedField) {
      const htmlElement = state.htmlElements.find(
        (e) => e.dataFieldName === currentSelectedField.name
      );
      if (htmlElement) {
        const iframe =
          document.getElementsByName("formMainFrame")[0]["contentDocument"];
        if (iframe) return iframe.getElementById(htmlElement.id) || null;
      }
    }
    return null;
  }
);
